const MONITORING_ENDPOINT = 'https://api.lineon.me/v1/monitoring/frontend-error'
const PROJECT = 'LineOnMe'
const SOURCE = 'web'
const DEDUP_WINDOW_MS = 15000

const recentFingerprints = new Map()

const normalizeMessage = (value) => String(value || '').replace(/\s+/g, ' ').trim().slice(0, 500)

const buildFingerprint = (payload) => {
  const raw = [
    payload.source || SOURCE,
    payload.category || 'frontend_js',
    normalizeMessage(payload.message || ''),
    payload.url || payload.endpoint || '',
    payload.statusCode || '',
  ].join('|')

  let hash = 0
  for (let i = 0; i < raw.length; i += 1) {
    hash = ((hash << 5) - hash) + raw.charCodeAt(i)
    hash |= 0
  }
  return `web_${Math.abs(hash)}`
}

const shouldSend = (fingerprint) => {
  const now = Date.now()
  const lastTs = recentFingerprints.get(fingerprint) || 0
  recentFingerprints.set(fingerprint, now)
  return now - lastTs > DEDUP_WINDOW_MS
}

const toJson = (value) => {
  try {
    return JSON.stringify(value)
  } catch {
    return ''
  }
}

const postEvent = (payload) => {
  const body = toJson(payload)
  if (!body) return

  try {
    if (navigator.sendBeacon && body.length < 60_000) {
      const blob = new Blob([body], { type: 'application/json' })
      navigator.sendBeacon(MONITORING_ENDPOINT, blob)
      return
    }
  } catch (error) {
    void error
  }

  fetch(MONITORING_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {})
}

export const reportFrontendError = (payload = {}) => {
  const event = {
    project: PROJECT,
    source: payload.source || SOURCE,
    severity: payload.severity || 'error',
    category: payload.category || 'frontend_js',
    message: normalizeMessage(payload.message || 'frontend_error'),
    url: payload.url || window.location.href,
    endpoint: payload.endpoint || '',
    method: payload.method || '',
    statusCode: payload.statusCode || 0,
    userId: payload.userId || null,
    clientId: payload.clientId || null,
    accountId: payload.accountId || null,
    route: payload.route || window.location.pathname,
    stack: payload.stack || '',
    runtime: 'browser',
    userAgent: navigator.userAgent,
    release: 'lineon_me',
    extra: payload.extra || null,
    timestamp: new Date().toISOString(),
  }

  event.fingerprint = payload.fingerprint || buildFingerprint(event)
  if (!shouldSend(event.fingerprint)) {
    return
  }

  postEvent(event)
}

const errorToText = (error) => {
  if (!error) return 'unknown_error'
  if (typeof error === 'string') return error
  return error.message || error.reason || error.type || 'unknown_error'
}

export const reportApiFailure = ({
  url = '',
  method = 'POST',
  statusCode = 0,
  message = '',
  requestId = '',
  responseBody = '',
  extra = null,
} = {}) => {
  reportFrontendError({
    category: statusCode >= 500 ? 'api_5xx' : 'api_network',
    severity: statusCode >= 500 ? 'error' : 'warning',
    message: message || (statusCode >= 500 ? `API ${statusCode}` : 'API network error'),
    url,
    method,
    statusCode,
    extra: {
      requestId,
      responseBody: typeof responseBody === 'string' ? responseBody.slice(0, 500) : responseBody,
      ...(extra || {}),
    },
  })
}

export const installGlobalErrorHandlers = (router) => {
  window.addEventListener('error', (event) => {
    const target = event?.target
    const isAssetError = target && target !== window && (target.src || target.href)

    reportFrontendError({
      category: isAssetError ? 'asset_load' : 'frontend_js',
      severity: 'error',
      message: event.message || errorToText(event.error),
      stack: event.error?.stack || '',
      url: isAssetError ? (target.src || target.href) : window.location.href,
      extra: isAssetError ? { tagName: target.tagName } : { file: event.filename, line: event.lineno, column: event.colno },
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    reportFrontendError({
      category: 'unhandled_promise',
      severity: 'error',
      message: errorToText(reason),
      stack: reason?.stack || '',
      extra: {
        reason: typeof reason === 'object' ? toJson(reason) : String(reason || ''),
      },
    })
  })

  if (router?.onError) {
    router.onError((error) => {
      reportFrontendError({
        category: 'chunk_load',
        severity: 'critical',
        message: errorToText(error),
        stack: error?.stack || '',
        extra: {
          route: router.currentRoute?.value?.fullPath || window.location.pathname,
        },
      })
    })
  }
}
